import React, { useState, useEffect } from 'react';
import { Calendar, Clock, User, ChevronLeft, ChevronRight, Phone, BedDouble } from 'lucide-react';
import { format, addDays, subDays, isSameDay } from 'date-fns';
import AdminLayout from '../../components/layout/AdminLayout';
import { useAuth } from '../../context/AuthContext';
import { bookingsAPI, stationsAPI } from '../../services/api';
import LoadingSpinner from '../../components/ui/LoadingSpinner';

const SLOT_LABELS = {
  '1hr': '1h',
  '3hr': '3h',
  '6hr': '6h',
  '9hr': '9h',
  '12hr': '12h',
  '24hr': '24h'
};

const AdvanceBookingsSchedule = () => {
  const { admin } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [stations, setStations] = useState([]);
  const [selectedStation, setSelectedStation] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (admin?.role === 'super_admin') {
      fetchStations();
    } else if (admin?.assignedStation) {
      setSelectedStation(admin.assignedStation._id);
    }
  }, [admin]);

  useEffect(() => {
    if (selectedStation) {
      fetchAdvanceBookings();
    }
  }, [selectedStation, selectedDate]);

  const fetchStations = async () => {
    try {
      const response = await stationsAPI.getAll();
      setStations(response.data.stations || []);
      if (response.data.stations?.length > 0) {
        setSelectedStation(response.data.stations[0]._id);
      }
    } catch (error) {
      console.error('Error fetching stations:', error);
    }
  };

  const fetchAdvanceBookings = async () => {
    if (!selectedStation) return;
    setLoading(true);
    try {
      const startDate = format(selectedDate, 'yyyy-MM-dd');
      const endDate = format(selectedDate, 'yyyy-MM-dd');
      const response = await bookingsAPI.getAdvanceBookings({
        station: selectedStation,
        startDate,
        endDate
      });
      setBookings(response.data.bookings || []);
    } catch (error) {
      console.error('Error fetching advance bookings:', error);
      setBookings([]);
    } finally {
      setLoading(false);
    }
  };

  const bookingsForDate = bookings.filter((b) => {
    const bDate = b.scheduledDate ? new Date(b.scheduledDate) : null;
    return bDate && isSameDay(bDate, selectedDate);
  });

  // Sort by time
  const sortedBookings = [...bookingsForDate].sort((a, b) => {
    const timeA = a.scheduledTimeSlot ? a.scheduledTimeSlot : '00:00';
    const timeB = b.scheduledTimeSlot ? b.scheduledTimeSlot : '00:00';
    return timeA.localeCompare(timeB);
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-amber-100 text-amber-800 border-amber-200';
      case 'approved': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'checked_in': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-midnight-100 text-midnight-700 border-midnight-200';
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-display font-bold text-midnight-900">
              Advance Booking Schedule
            </h1>
            <p className="text-midnight-600 mt-1">
              View all scheduled bookings across the day (15-min slots)
            </p>
          </div>

          {admin?.role === 'super_admin' && (
            <select
              value={selectedStation}
              onChange={(e) => setSelectedStation(e.target.value)}
              className="input-field max-w-xs"
            >
              {stations.map((s) => (
                <option key={s._id} value={s._id}>
                  {s.name} ({s.code})
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Date navigator */}
        <div className="card p-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setSelectedDate(subDays(selectedDate, 1))}
              className="p-2 rounded-lg hover:bg-midnight-50 transition-colors"
            >
              <ChevronLeft className="w-5 h-5 text-midnight-600" />
            </button>
            <div className="flex items-center gap-3">
              <Calendar className="w-6 h-6 text-railway-500" />
              <span className="text-xl font-semibold text-midnight-900">
                {format(selectedDate, 'EEEE, MMMM d, yyyy')}
              </span>
            </div>
            <button
              onClick={() => setSelectedDate(addDays(selectedDate, 1))}
              className="p-2 rounded-lg hover:bg-midnight-50 transition-colors"
            >
              <ChevronRight className="w-5 h-5 text-midnight-600" />
            </button>
          </div>
        </div>

        {/* Timeline view */}
        <div className="card overflow-hidden">
          <div className="p-4 border-b border-midnight-100 bg-gradient-to-r from-midnight-50 to-white">
            <h2 className="text-lg font-display font-semibold text-midnight-900 flex items-center gap-2">
              <Clock className="w-5 h-5 text-railway-500" />
              Daily Schedule
            </h2>
            <p className="text-sm text-midnight-500 mt-1">
              Bookings shown with check-in time • 15-min gap between slots
            </p>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <LoadingSpinner size="lg" />
            </div>
          ) : sortedBookings.length === 0 ? (
            <div className="text-center py-20">
              <Calendar className="w-16 h-16 text-midnight-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-midnight-700">No advance bookings</h3>
              <p className="text-midnight-500 mt-2">
                No scheduled bookings for {format(selectedDate, 'MMM d, yyyy')}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-midnight-100">
              {sortedBookings.map((booking) => (
                <div
                  key={booking._id}
                  className="p-6 hover:bg-midnight-50/50 transition-colors"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="flex items-start gap-4">
                      <div className="w-14 h-14 rounded-xl bg-railway-100 flex flex-col items-center justify-center flex-shrink-0">
                        <Clock className="w-6 h-6 text-railway-500 mb-0.5" />
                        <span className="text-xs font-bold text-railway-700">
                          {booking.scheduledTimeSlot || '—'}
                        </span>
                      </div>
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold text-midnight-900 text-lg">
                            {booking.guestName}
                          </h3>
                          <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(booking.status)}`}>
                            {booking.status.replace('_', ' ')}
                          </span>
                        </div>
                        <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-midnight-600">
                          <span className="flex items-center gap-1">
                            <Phone className="w-3.5 h-3.5" />
                            {booking.countryCode || '+91'} {booking.mobile}
                          </span>
                          <span className="flex items-center gap-1">
                            <BedDouble className="w-3.5 h-3.5" />
                            {SLOT_LABELS[booking.durationSlot] || booking.durationSlot}
                          </span>
                          <span>
                            {booking.requestedCategory?.name || booking.allocatedCategory?.name || 'Any'}
                          </span>
                          {booking.allocatedRoom && (
                            <span className="font-medium text-railway-600">
                              Room {booking.allocatedRoom.roomNumber}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-midnight-500">PNR</div>
                      <div className="font-mono font-semibold text-midnight-900">
                        {booking.prn}
                      </div>
                      {booking.scheduledTimeSlot && booking.duration && (
                        <div className="text-xs text-midnight-400 mt-1">
                          Ends ~{(() => {
                            const [h, m] = booking.scheduledTimeSlot.split(':').map(Number);
                            const totalMins = h * 60 + m + booking.duration * 60;
                            const endH = Math.floor(totalMins / 60) % 24;
                            const endM = Math.floor(totalMins % 60);
                            return `${String(endH).padStart(2, '0')}:${String(endM).padStart(2, '0')}`;
                          })()}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdvanceBookingsSchedule;
